import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress, Link as MuiLink, Chip,
} from '@mui/material';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { signUp, confirmSignUp, resendConfirmationCode } from '../services/cognitoService';
import { getMarketplaceOnboarding, hasMarketplaceOnboarding } from '../services/marketplaceOnboardingService';

export default function RegisterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState('register'); // 'register' | 'confirm'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const fromMarketplace = new URLSearchParams(location.search).get('fromMarketplace') === '1';
  const marketplaceOnboarding = getMarketplaceOnboarding();

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (fromMarketplace && !hasMarketplaceOnboarding()) {
      setError('Marketplace session not found. Restart the AWS Marketplace setup flow.');
    }
  }, [fromMarketplace]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPw) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      if (fromMarketplace && !marketplaceOnboarding?.customerIdentifier) {
        throw new Error('Marketplace session not found. Restart the AWS Marketplace setup flow.');
      }

      await signUp(email, password, email);
      setStep('confirm');
      setSuccess('Registration successful! Check your email for the verification code.');
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmSignUp(email, code);

      setSuccess('Email confirmed! You can now sign in.');
      setTimeout(() => navigate(`/login${fromMarketplace ? `?fromMarketplace=1&email=${encodeURIComponent(email)}` : ''}`, { replace: true }), 2000);
    } catch (err) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      await resendConfirmationCode(email);
      setSuccess('Verification code resent to your email.');
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    }
  };

  if (step === 'confirm') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <Card sx={{ maxWidth: 400, width: '100%', p: 2 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>Verify Email</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Enter the verification code sent to <strong>{email}</strong>
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <form onSubmit={handleConfirm}>
              <TextField
                label="Verification Code" fullWidth margin="normal"
                value={code} onChange={(e) => setCode(e.target.value)} required autoFocus
              />
              <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Confirm'}
              </Button>
            </form>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <MuiLink component="button" variant="body2" onClick={handleResend}>
                Resend verification code
              </MuiLink>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <Card sx={{ maxWidth: 400, width: '100%', p: 2 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>Register</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>VIC Services API Management</Typography>
          {fromMarketplace && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Marketplace validation is complete. Continue registering this account to finish onboarding.
              {marketplaceOnboarding?.customerIdentifier ? ` Customer ID: ${marketplaceOnboarding.customerIdentifier}` : ''}
              {marketplaceOnboarding?.offerType === 'free-trial' && (
                <Chip label="Free Trial" color="success" size="small" sx={{ ml: 1 }} />
              )}
            </Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <form onSubmit={handleRegister}>
            <TextField
              label="Email" type="email" fullWidth margin="normal"
              value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
            />
            <TextField
              label="Password" type="password" fullWidth margin="normal"
              value={password} onChange={(e) => setPassword(e.target.value)} required
              helperText="Min 8 chars, uppercase, lowercase, number, special char"
            />
            <TextField
              label="Confirm Password" type="password" fullWidth margin="normal"
              value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required
            />
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Register'}
            </Button>
          </form>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              Already have an account?{' '}
              <MuiLink component={Link} to={fromMarketplace ? '/login?fromMarketplace=1' : '/login'}>Sign in</MuiLink>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
